import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Paper, Stack, Chip, Grid, Badge, ListItemIcon} from 'views/components';
import {Translate, translate} from 'views/module';

import moment from 'moment';

const JobInformation = (props) => {
  const {jobData, client} = props;

  const isOvernightShift = (startTime, endTime) => {
    if (!startTime || !endTime) return false;
    const start = moment(startTime);
    const end = moment(endTime);

    const startDay = start.clone().startOf('day');
    const endDay = end.clone().startOf('day');

    return endDay.isAfter(startDay);
  };

  const weeklyHours = React.useMemo(() => {
    if (jobData?.schedule?.type === 'LIVEIN_EVENT') {
      const hours = 7 * 8;
      return parseFloat(hours.toFixed(2));
    }
    const {weeklySlots} = jobData.schedule.recurrenceRule;
    const totalHours = weeklySlots.reduce((total, slot) => {
      const start = moment(slot.startTime);
      const end = moment(slot.endTime);

      const hoursPerSlot = end.diff(start, 'hours', true);
      return total + Math.abs(hoursPerSlot);
    }, 0);
    return parseFloat(totalHours.toFixed(2));
  }, [jobData.schedule]);

  const formatSchedule = React.useMemo(() => {
    if (!jobData.schedule || !jobData.schedule.recurrenceRule) return '';
    const {weeklySlots} = jobData.schedule.recurrenceRule;
    return weeklySlots.map((slot) => {
      const dayName = translate(`career.jobDetail.Day.${slot.day}`);
      const startTime = moment(slot.startTime).format('h:mm A');
      const endTime = moment(slot.endTime).format('h:mm A');
      const overnightIndicator = isOvernightShift(slot.startTime, slot.endTime) ? ' (overnight)' : '';
      return `${dayName}${overnightIndicator}: ${startTime} - ${endTime}`;
    }).join('\n');
  }, [jobData.schedule, isOvernightShift]);

  const hourlyRate = parseFloat(jobData.price.hourlyRate);
  const weeklyEarnings = weeklyHours !== null ? parseFloat((hourlyRate * weeklyHours).toFixed(2)) : null;
  return (
    <Box sx={{p: 3}}>

      <Stack spacing={3}>
        <Paper sx={{p: 3, borderRadius: 5}}>
          <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
            <Translate expression="career.jobDetail.Information.AboutThisRole" />
          </Typography>

          <Box sx={{mb: 2}}>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
              <Translate expression="career.jobDetail.Information.ClientFor" />
            </Typography>
            <Typography variant="body1" sx={{mb: 2}}>
              {client?.clientDetail?.clientName || jobData.client?.unregisteredClient || 'Unknown Client'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
              <Translate expression="career.jobDetail.Information.Description" />
            </Typography>
            {
              jobData.description?.long?.blob &&
              <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap'}}>
                {jobData.description.long.blob}
              </Typography>
            }
          </Box>
        </Paper>

        <Grid container spacing={0}>
          <Grid item xs={12} sm={6} sx={{pr: {sm: 1.5}}}>
            <Paper sx={{height: '100%', minHeight: '140px', p: 3, borderRadius: 5}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.jobDetail.Information.Schedule" />
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{fontSize: '15px', whiteSpace: 'pre-line'}}>
                {formatSchedule}
              </Typography>
              {
                jobData.schedule.type === 'LIVEIN_EVENT' && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                      <Translate expression="career.jobDetail.Information.LiveIn" />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                      <Translate expression="career.jobDetail.Information.WeeklyHours" substitute={{hours: weeklyHours}} />
                    </Typography>
                  </Stack>
                )
              }
              {
                jobData.schedule.type === 'RECURRENCE_EVENT' && (
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                    <Translate expression="career.jobDetail.Information.WeeklyHours" substitute={{hours: weeklyHours}} />
                  </Typography>
                )
              }
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} sx={{pl: {sm: 1.5}, mt: {xs: 3, sm: 0}}}>
            <Paper sx={{height: '100%', bgcolor: '#fef7f7', minHeight: '140px', p: 3, borderRadius: 5}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.jobDetail.Information.Compensation" />
              </Typography>
              <Typography variant="h4" sx={{fontWeight: 'bold', color: '#dc2626', mb: 1}}>
                <Translate expression="career.jobDetail.Information.HourlyRate" substitute={{rate: hourlyRate}} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{fontSize: '16px'}}>
                <Translate expression="career.jobDetail.Information.WeeklyEarnings" substitute={{amount: weeklyEarnings}} />
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{p: 3, borderRadius: 5}}>
          <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
            <Translate expression="career.jobDetail.Information.CareNeeds" />
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {
              jobData.requirements.careNeeds.map((need) => (
                <Chip
                  key={need}
                  label={translate(`career.jobDetail.Information.CarePlan.${need}.Label`)}
                  variant="filled"
                  sx={{bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 500, mb: 1}}
                />
              ))
            }
          </Stack>
        </Paper>

        <Paper sx={{p: 3, borderRadius: 5}}>
          <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
            <Translate expression="career.jobDetail.Information.Requirements" />
          </Typography>
          {/* Certifications grouped similarly to Review.jsx */}
          <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
            <Translate expression="career.create.Requirements.CertificationLabel" />
          </Typography>
          {
            (jobData.requirements.certifications || []).length > 0 &&
              <Box sx={{mb: 1}}>
                <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                  {translate('career.create.Requirements.ColorKeyMandatory')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {
                    (jobData.requirements.certifications).map((cert) => (
                      <Chip
                        key={cert}
                        label={translate(`career.jobDetail.Information.CertificationsOptions.${cert}`)}
                        variant="filled"
                        sx={{bgcolor: '#FCF2E8', color: '#FC8301', fontWeight: 500, mb: 1}}
                      />
                    ))
                  }
                </Stack>
              </Box>
          }

          {
            (jobData.requirements.atLeastOneCertifications || []).length > 0 &&
              <Box sx={{mb: 1}}>
                <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                  {translate('career.create.Requirements.ColorKeyAtLeastOne')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {
                    (jobData.requirements.atLeastOneCertifications).map((cert) => (
                      <Chip
                        key={cert}
                        label={translate(`career.jobDetail.Information.CertificationsOptions.${cert}`)}
                        variant="filled"
                        sx={{bgcolor: '#FEE5E6', color: '#F9575C', fontWeight: 500, mb: 1}}
                      />
                    ))
                  }
                </Stack>
              </Box>
          }

          {
            (jobData.requirements.niceToHaveCertifications || []).length > 0 &&
              <Box sx={{mb: 1}}>
                <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                  {translate('career.create.Requirements.ColorKeyNiceToHave')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {
                    (jobData.requirements.niceToHaveCertifications || []).map((cert) => (
                      <Chip
                        key={cert}
                        label={translate(`career.jobDetail.Information.CertificationsOptions.${cert}`)}
                        variant="filled"
                        sx={{bgcolor: '#FFF8E6', color: '#FFB400', fontWeight: 500, mb: 1}}
                      />
                    ))
                  }
                </Stack>
              </Box>
          }

          {/* Languages grouped similarly to Review.jsx */}
          {
            ((jobData.requirements.languages || []).length > 0 || (jobData.requirements.niceToHaveLanguages || []).length > 0) &&
            <Box>
              <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
                <Translate expression="career.create.Requirements.LanguagesDisplayLabel" />
              </Typography>

              {
                (jobData.requirements.languages || []).length > 0 &&
                <Box sx={{mb: 1}}>
                  <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                    {translate('career.create.Requirements.ColorKeyMandatory')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {
                      (jobData.requirements.languages || []).map((lang) => (
                        <Chip
                          key={lang}
                          label={translate(`career.jobDetail.Information.LanguageOptions.${lang}`)}
                          variant="filled"
                          sx={{bgcolor: '#FCF2E8', color: '#FC8301', fontWeight: 500, mb: 1}}
                        />
                      ))
                    }
                  </Stack>
                </Box>
              }

              {
                (jobData.requirements.niceToHaveLanguages || []).length > 0 &&
                <Box>
                  <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                    {translate('career.create.Requirements.ColorKeyNiceToHave')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {
                      (jobData.requirements.niceToHaveLanguages || []).map((lang) => (
                        <Chip
                          key={lang}
                          label={translate(`career.jobDetail.Information.LanguageOptions.${lang}`)}
                          variant="filled"
                          sx={{bgcolor: '#FFF8E6', color: '#FFB400', fontWeight: 500, mb: 1}}
                        />
                      ))
                    }
                  </Stack>
                </Box>
              }
            </Box>
          }
        </Paper>
        {
          jobData.priorities &&
          <Paper sx={{p: 3, borderRadius: 5}}>
            <>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.jobDetail.Information.Priorities" />
              </Typography>
              <Stack spacing={1}>
                {
                  (() => {
                    const attributeLabelMap = {
                      HOURLY_RATE: translate('career.create.Price.HourlyRateLabel'),
                      START_DATE: translate('career.create.StartDate.StartDate'),
                      CERTIFICATIONS: translate('career.create.Requirements.CertificationLabel'),
                      LANGUAGES: translate('career.create.Requirements.LanguagesDisplayLabel'),
                      CONSIDERATIONS: translate('career.create.Considerations.Considerations'),
                    };

                    return jobData.priorities.map((key, index) => (
                      <Stack key={`${key}-${index}`} sx={{p: 1}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ListItemIcon sx={{display: 'flex', justifyContent: 'center'}}>
                            <Badge
                              badgeContent={index + 1}
                              color="primary"
                              data-testid={`priorityBadge-${index}`}
                              aria-label={`priority-${index + 1}`}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '16px',
                                  height: '32px',
                                  width: '32px',
                                  borderRadius: '20px',
                                  bgcolor: 'primary',
                                },
                              }}
                            />
                          </ListItemIcon>
                          <Typography>
                            {attributeLabelMap[key] || key}
                          </Typography>
                        </Stack>
                      </Stack>
                    ));
                  })()
                }
              </Stack>
            </>
          </Paper>
        }
        <Paper sx={{p: 3, borderRadius: 5}}>
          <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
            <Translate expression="career.jobDetail.Information.SpecialConsiderations" />
          </Typography>
          <Stack spacing={2}>
            {
              jobData.requirements.considerations?.length > 0 &&
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {
                  jobData.requirements.considerations.map((option) => (
                    <Chip
                      key={option}
                      label={translate(`career.jobDetail.Information.ConsiderationsOptions.${option}`)}
                      variant="filled"
                      sx={{bgcolor: '#fff3e0', color: '#ef6c00', fontWeight: 500, mb: 1}}
                    />
                  ))
                }
              </Stack>
            }
            {
              jobData.requirements.additionalConsiderations?.blob &&
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600}}>
                  <Translate expression="career.jobDetail.Information.AdditionalConsiderations" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {jobData.requirements.additionalConsiderations.blob}
                </Typography>
              </Box>
            }
            {
              (!jobData.requirements.considerations?.length && !jobData.requirements.additionalConsiderations?.blob) &&
              <Typography variant="body2" color="text.secondary">
                <Translate expression="career.jobDetail.Information.NoSpecialConsiderations" />
              </Typography>
            }
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

JobInformation.propTypes = {
  jobData: PropTypes.object.isRequired,
  client: PropTypes.shape({
    clientDetail: PropTypes.shape({
      clientName: PropTypes.string,
    }),
  }),
};

export default JobInformation;
